import {
  APP_INITIALIZER,
  ApplicationConfig,
  forwardRef,
  importProvidersFrom,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';

import { ApiConfiguration } from './api/api-configuration';

import { provideRouter } from '@angular/router';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { routes } from './app.routes';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { ApiInterceptor } from './core/interceptors/api.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { CookieService } from 'ngx-cookie-service';

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true,
};

export const MSAL_INSTANCE_PROVIDER: Provider = {
  provide: MSAL_INSTANCE,
  useFactory: MSALInstanceFactory,
};

export const MSAL_GAURD_CONFIG_PROVIDER: Provider = {
  provide: MSAL_GUARD_CONFIG,
  useFactory: MSALGuardConfigFactory,
};

export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.singleSignOnConfig.loginCallbackUrl,
      postLogoutRedirectUri: environment.singleSignOnConfig.logoutCallbackUrl,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login', // Stay in login page if failed
  };
}

export function msalInitializer(msal: MsalService) {
  // wait until observable for initialization emits and completes
  return () => msal.initialize();
}

// For initializing msal before use
export const AppInitializerProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: msalInitializer,
  deps: [MsalService],
  multi: true,
};

export const APIProvider: Provider = {
  provide: ApiConfiguration,
  useValue: { rootUrl: environment.apiConfig.uri },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      MatButtonModule,
      MatToolbarModule,
      MatListModule,
      MatMenuModule
    ),
    CookieService,
    AppInitializerProvider,
    MSAL_INSTANCE_PROVIDER,
    MSAL_GAURD_CONFIG_PROVIDER,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ApiInterceptor,
    API_INTERCEPTOR_PROVIDER,
    APIProvider,
  ],
};
